import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, CheckCircle2, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { IconInput, PasswordInput } from "@/pages/Login";

const Shell = ({ title, subtitle, children, testid }) => (
  <main data-testid={testid} className="mx-auto max-w-md px-5 py-20">
    <div className="card-gold p-8">
      <h1 className="text-center text-2xl font-bold">{title}</h1>
      {subtitle && <p className="mt-2 text-center text-sm text-cr-ink/75">{subtitle}</p>}
      <div className="mt-8">{children}</div>
    </div>
  </main>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
    } catch (e2) { setErr(apiError(e2)); }
  };
  return (
    <Shell testid="forgot-page" title="Forgot Password" subtitle="Enter your account email and we'll send a reset link.">
      {msg ? <p data-testid="forgot-success" className="rounded-md border border-cr-success/40 bg-cr-success/10 p-3 text-sm text-cr-success">{msg} (Email delivery is mocked in this preview.)</p> : (
        <form onSubmit={submit} className="space-y-4">
          <IconInput icon={Mail} type="email" required data-testid="forgot-email-input" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          {err && <p data-testid="forgot-error" className="text-sm text-cr-danger">{err}</p>}
          <button data-testid="forgot-submit-btn" className="btn-gold w-full py-3">Send reset link</button>
        </form>
      )}
      <p className="mt-6 text-center text-sm"><Link to="/login" data-testid="forgot-back-login" className="text-cr-gold">Back to login</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("Passwords do not match");
    try {
      await api.post("/auth/reset-password", { token: sp.get("token") || "", password: pw });
      setDone(true);
    } catch (e2) { setErr(apiError(e2)); }
  };
  return (
    <Shell testid="reset-page" title="Reset Password" subtitle="Choose a new password for your account.">
      {done ? <p data-testid="reset-success" className="text-center text-cr-success">Password updated. <Link to="/login" className="text-cr-gold underline">Login</Link></p> : (
        <form onSubmit={submit} className="space-y-4">
          <PasswordInput testid="reset-password-input" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <PasswordInput testid="reset-confirm-input" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
          {err && <p data-testid="reset-error" className="text-sm text-cr-danger">{err}</p>}
          <button data-testid="reset-submit-btn" className="btn-gold w-full py-3">Update password</button>
        </form>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const [state, setState] = useState("loading");
  const [err, setErr] = useState("");
  useEffect(() => {
    api.post("/auth/verify-email/confirm", { token: sp.get("token") || "" })
      .then(() => setState("ok")).catch((e) => { setErr(apiError(e)); setState("fail"); });
  }, [sp]);
  return (
    <Shell testid="verify-page" title="Email Verification">
      {state === "loading" && <p className="text-center text-cr-muted">Verifying…</p>}
      {state === "ok" && <div data-testid="verify-success" className="text-center"><CheckCircle2 className="mx-auto h-12 w-12 text-cr-success" /><p className="mt-3">Your email is verified.</p><Link to="/account" className="btn-gold mt-6">Go to dashboard</Link></div>}
      {state === "fail" && <div data-testid="verify-fail" className="text-center"><XCircle className="mx-auto h-12 w-12 text-cr-danger" /><p className="mt-3 text-cr-danger">{err}</p></div>}
    </Shell>
  );
}
